import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLatestPredictionResults, predictAPI } from '../api';
import PredictionAnnotation from './PredictionAnnotation';
import { InfoTooltip, ErrorState, EmptyState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

function PredictionTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [annotations, setAnnotations] = useState({});
  const [currentPredictionFile, setCurrentPredictionFile] = useState(null);
  const [singlePrediction, setSinglePrediction] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrediction, setFilterPrediction] = useState('all');
  const [filterAnnotation, setFilterAnnotation] = useState('all');
  const [sortBy, setSortBy] = useState('compound_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Function to refresh data (can be called from parent components)
  const refreshData = useCallback(async () => {
    console.log('Refresh data button clicked - fetching latest prediction results...');
    setLoading(true);
    setError(null);
    try {
      const predictions = await getLatestPredictionResults();
      console.log('Refresh data - received predictions:', predictions.length, 'records');
      console.log('Refresh data - first prediction:', predictions[0]);
      setData(predictions);
      // Clear current prediction file since we're getting the latest
      setCurrentPredictionFile(null);
    } catch (err) {
      console.warn('Failed to refresh data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for batch prediction completion events
  useEffect(() => {
    const handleBatchPredictionCompleted = async (event) => {
      console.log('Batch prediction completed event received:', event.detail);
      
      // If we have a specific results filename, use that instead of the latest file
      if (event.detail && event.detail.resultsFilename) {
        console.log('Loading specific prediction results from:', event.detail.resultsFilename);
        try {
          setLoading(true);
          setError(null);
          const predictions = await predictAPI.getPredictionResultsFromFile(event.detail.resultsFilename);
          setData(predictions);
          setCurrentPredictionFile(event.detail.resultsFilename);
          console.log('Successfully loaded prediction results from specific file');
        } catch (err) {
          console.warn('Failed to load specific prediction file, falling back to latest:', err);
          // Fallback to latest file if specific file fails
          refreshData();
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback to latest file if no specific filename provided
        console.log('No specific filename provided, refreshing with latest data...');
        refreshData();
      }
    };

    window.addEventListener('batchPredictionCompleted', handleBatchPredictionCompleted);

    return () => {
      window.removeEventListener('batchPredictionCompleted', handleBatchPredictionCompleted);
    };
  }, [refreshData]);

  // Show a manually entered prediction as a row in this table
  useEffect(() => {
    const handleSinglePredictionCompleted = (event) => {
      if (event?.detail?.row) setSinglePrediction(event.detail.row);
    };
    window.addEventListener('singlePredictionCompleted', handleSinglePredictionCompleted);
    return () => {
      window.removeEventListener('singlePredictionCompleted', handleSinglePredictionCompleted);
    };
  }, []);



  const handleAnnotationChange = (compoundId, annotation) => {
    setAnnotations(prev => ({
      ...prev,
      [compoundId]: annotation
    }));
  };

  // Loaded rows define the columns; a manual prediction only does so when nothing is loaded,
  // otherwise adding one would reshape the table around a single row.
  const columnSource = data[0] || singlePrediction || null;

  const featureColumns = useMemo(() => {
    if (!columnSource?.feature_values) return [];
    return Object.keys(columnSource.feature_values);
  }, [columnSource]);

  // Get unique prediction values for filter options
  const predictionOptions = useMemo(() => {
    const uniquePredictions = [...new Set(data.map(item => item.prediction))];
    return uniquePredictions.sort();
  }, [data]);

  // Check if data has Patient ID column
  const hasPatientId = useMemo(() => {
    return Boolean(columnSource) && columnSource.compound_id !== null;
  }, [columnSource]);

  // Get the actual column header name for the ID column
  const idColumnHeader = useMemo(() => {
    return columnSource?.compound_id_header || 'Patient ID';
  }, [columnSource]);

  // Check if data has confidence scores
  const hasConfidence = useMemo(() => {
    const loadedHasConfidence = data.length > 0 && data[0].confidence_score != null;
    return loadedHasConfidence || singlePrediction?.confidence_score != null;
  }, [data, singlePrediction]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item => {
      // Search filter - search in compound_id (if exists) and feature values
      const matchesSearch = searchTerm === '' || 
        (item.compound_id && item.compound_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.feature_values && Object.values(item.feature_values).some(value => 
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        ));
      
      // Prediction filter
      const matchesPrediction = filterPrediction === 'all' || item.prediction === filterPrediction;
      
      // Annotation filter
      const itemAnnotation = item.compound_id ? annotations[item.compound_id]?.annotation : null;
      const matchesAnnotation = filterAnnotation === 'all' || itemAnnotation === filterAnnotation;
      
      return matchesSearch && matchesPrediction && matchesAnnotation;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy.startsWith('feature_')) {
        // Handle dynamic feature columns
        const featureIndex = parseInt(sortBy.replace('feature_', ''));
        const featureName = featureColumns[featureIndex];
        if (featureName && a.feature_values && b.feature_values) {
          aValue = a.feature_values[featureName] || 0;
          bValue = b.feature_values[featureName] || 0;
        } else {
          aValue = 0;
          bValue = 0;
        }
      } else {
        switch (sortBy) {
          case 'compound_id':
            aValue = a.compound_id;
            bValue = b.compound_id;
            break;
          case 'prediction':
            aValue = a.prediction;
            bValue = b.prediction;
            break;
          case 'confidence_score':
            aValue = a.confidence_score;
            bValue = b.confidence_score;
            break;
          default:
            aValue = a.compound_id;
            bValue = b.compound_id;
        }
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [data, searchTerm, filterPrediction, filterAnnotation, annotations, sortBy, sortOrder, featureColumns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterPrediction, filterAnnotation, sortBy, sortOrder, itemsPerPage, data.length]);

  const totalRows = filteredAndSortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(startIndex, endIndex);
  const shownStart = totalRows > 0 ? startIndex + 1 : 0;
  const shownEnd = Math.min(endIndex, totalRows);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span style={{ color: 'var(--text-muted)' }}>↕</span>;
    return sortOrder === 'asc' ? <span style={{ color: 'var(--primary)' }}>↑</span> : <span style={{ color: 'var(--primary)' }}>↓</span>;
  };

  const getPredictionBadgeClass = (prediction) => {
    if (prediction === 'True') return 'true';
    if (prediction === 'False') return 'false';
    if (prediction === '0' || prediction === '1') return prediction === '1' ? 'true' : 'false';
    return 'numeric'; // For regression predictions
  };

  // Regression predictions arrive as full-precision strings - format them to 3 decimal places for display
  const formatPrediction = (prediction) => {
    const asNumber = Number(prediction);
    if (prediction === '' || prediction == null || Number.isNaN(asNumber)) return prediction;
    return Number.isInteger(asNumber) ? String(asNumber) : asNumber.toFixed(3);
  };

  // Shared by the loaded rows and the pinned manual prediction so both render identically
  const renderRow = (item, index) => {
    if (!item) return null;
    const rowKey = item.is_single_prediction ? 'single-prediction' : (item.compound_id || index);
    const annotationId = item.is_single_prediction
      ? 'single_prediction'
      : (item.compound_id || `row_${index}`);
    return (
      <tr
        key={rowKey}
        className={`prediction-table__table-row ${index % 2 === 0 ? 'prediction-table__table-row--even' : 'prediction-table__table-row--odd'} ${item.is_single_prediction ? 'prediction-table__table-row--single' : ''}`}
      >
        {hasPatientId && (
          <td className="prediction-table__table-cell prediction-table__table-cell--id">
            {item.compound_id}
          </td>
        )}
        {/* Dynamic feature columns */}
        {featureColumns.slice(0, 5).map((featureName) => (
          <td key={featureName} className="prediction-table__table-cell prediction-table__table-cell--feature">
            {item.feature_values && item.feature_values[featureName] !== undefined
              ? (typeof item.feature_values[featureName] === 'number'
                  ? item.feature_values[featureName].toFixed(3)
                  : item.feature_values[featureName])
              : 'N/A'
            }
          </td>
        ))}
        <td className="prediction-table__table-cell">
          <span className={`prediction-table__prediction-badge prediction-table__prediction-badge--${getPredictionBadgeClass(item.prediction)}`}>
            {formatPrediction(item.prediction)}
          </span>
        </td>
        {hasConfidence && (
          <td className="prediction-table__table-cell">
            {item.confidence_score != null ? (
              <div className="prediction-table__confidence">
                <span className="prediction-table__confidence-value">
                  {(item.confidence_score * 100).toFixed(1)}%
                </span>
                <div className="prediction-table__confidence-bar">
                  <div
                    className="prediction-table__confidence-fill"
                    style={{ width: `${(item.confidence_score * 100)}%` }}
                  ></div>
                </div>
              </div>
            ) : 'N/A'}
          </td>
        )}
        <td className="prediction-table__table-cell">
          <PredictionAnnotation
            compoundId={annotationId}
            onAnnotationChange={(annotation) => handleAnnotationChange(annotationId, annotation)}
          />
        </td>
      </tr>
    );
  };

  if (loading) {
    return <LoadingSpinner text="Loading predictions..." />;
  }

  // A failed file load should not discard a manual prediction the user just ran
  if (error && !singlePrediction) {
    return <ErrorState message={error} />;
  }

  if (!data.length && !singlePrediction) {
    return (
      <EmptyState
        title="No predictions yet"
        description="Run a single or batch prediction above and the results will appear here."
        icon="📊"
        actionLabel="Load latest saved results"
        actionAriaLabel="Load the most recent saved batch prediction results"
        onAction={refreshData}
      />
    );
  }

  return (
    <div className="prediction-table">
      <InfoTooltip 
        title="Prediction Results" 
        content="View and annotate model predictions. Use search and filters to find specific compounds or predictions."
      >
        <h2 className="prediction-table__title">Prediction Results</h2>
      </InfoTooltip>
      
      <div className="prediction-table__guide">
        <p className="prediction-table__guide-text">
          <strong>💡 Annotation Guide:</strong> Tag predictions as 'Accurate', 'Review Needed', or 'Incorrect' 
          to help improve model performance and track prediction quality.
        </p>
      </div>

      {data.length > 0 && (
        <div className="prediction-table__data-source">
          <div className="prediction-table__info">
            <p className="prediction-table__info-text">
              📊 {currentPredictionFile
                ? `Showing results from: ${currentPredictionFile}`
                : 'Showing the most recent saved batch prediction results'
              }
            </p>
            <button
              onClick={refreshData}
              disabled={loading}
              className="btn btn--secondary btn--small"
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="prediction-table__controls">
        {/* Search Input */}
        <div className="prediction-table__control-group">
          <label className="prediction-table__control-label">Search</label>
          <input
            type="text"
            placeholder={`Search by ${idColumnHeader} or feature values...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="prediction-table__control-input"
          />
        </div>

        {/* Prediction Filter */}
        <div className="prediction-table__control-group">
          <label className="prediction-table__control-label">Prediction</label>
          <select
            value={filterPrediction}
            onChange={(e) => setFilterPrediction(e.target.value)}
            className="prediction-table__control-select"
          >
            <option value="all">All Predictions</option>
            {predictionOptions.map(prediction => (
              <option key={prediction} value={prediction}>{prediction}</option>
            ))}
          </select>
        </div>

        {/* Annotation Filter */}
        <div className="prediction-table__control-group">
          <label className="prediction-table__control-label">Annotation Status</label>
          <select
            value={filterAnnotation}
            onChange={(e) => setFilterAnnotation(e.target.value)}
            className="prediction-table__control-select"
          >
            <option value="all">All</option>
            <option value="accurate">Accurate</option>
            <option value="review_needed">Review Needed</option>
            <option value="incorrect">Incorrect</option>
            <option value="unannotated">Unannotated</option>
          </select>
        </div>

        {/* Results Count */}
        <div className="prediction-table__control-group">
          <label className="prediction-table__control-label">Results</label>
          <div className="prediction-table__results-count">
            {shownStart}-{shownEnd} out of {totalRows}
          </div>
        </div>

        <div className="prediction-table__control-group">
          <label className="prediction-table__control-label">Rows per page</label>
          <input
            type="number"
            min="1"
            max="200"
            value={itemsPerPage}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isFinite(next) && next > 0) {
                setItemsPerPage(next);
              }
            }}
            className="prediction-table__control-input"
          />
        </div>
      </div>
      
      {/* Table */}
      <div className="prediction-table__table-container">
        <table className="prediction-table__table">
          <thead className="prediction-table__table-header">
            <tr>
              {hasPatientId && (
                <th 
                  className="prediction-table__table-header-cell prediction-table__table-header-cell--sortable"
                  onClick={() => handleSort('compound_id')}
                >
                  <div className="prediction-table__table-header-content">
                    {idColumnHeader}
                    <SortIcon column="compound_id" />
                  </div>
                </th>
              )}
              {/* Dynamic feature columns */}
              {featureColumns.slice(0, 5).map((featureName, index) => (
                <th 
                  key={featureName}
                  className="prediction-table__table-header-cell prediction-table__table-header-cell--sortable"
                  onClick={() => handleSort(`feature_${index}`)}
                >
                  <div className="prediction-table__table-header-content">
                    {featureName}
                    <SortIcon column={`feature_${index}`} />
                  </div>
                </th>
              ))}
              <th 
                className="prediction-table__table-header-cell prediction-table__table-header-cell--sortable"
                onClick={() => handleSort('prediction')}
              >
                <div className="prediction-table__table-header-content">
                  Prediction
                  <SortIcon column="prediction" />
                </div>
              </th>
              {hasConfidence && (
                <th 
                  className="prediction-table__table-header-cell prediction-table__table-header-cell--sortable"
                  onClick={() => handleSort('confidence_score')}
                >
                  <div className="prediction-table__table-header-content">
                    Confidence
                    <SortIcon column="confidence_score" />
                  </div>
                </th>
              )}
              <th className="prediction-table__table-header-cell">
                Annotation
              </th>
            </tr>
          </thead>
          <tbody>
            {renderRow(singlePrediction, 0)}
            {paginatedData.map((item, index) => renderRow(item, index + (singlePrediction ? 1 : 0)))}
          </tbody>
        </table>
      </div>

      {totalRows > 0 && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            Showing {shownStart}-{shownEnd} out of {totalRows}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button
              className="btn btn--small"
              disabled={safeCurrentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--white)',
                border: '1px solid var(--primary)',
                opacity: safeCurrentPage === 1 ? 0.6 : 1,
              }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={page === safeCurrentPage ? 'btn btn--primary btn--small' : 'btn btn--outline btn--small'}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="btn btn--small"
              disabled={safeCurrentPage === totalPages}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--white)',
                border: '1px solid var(--primary)',
                opacity: safeCurrentPage === totalPages ? 0.6 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="prediction-table__summary">
        <h4 className="prediction-table__summary-title">
          Annotation Summary
        </h4>
        <div className="prediction-table__summary-grid">
          <div className="prediction-table__summary-item">
            <div className="prediction-table__summary-value">{data.length}</div>
            <div className="prediction-table__summary-label">Total Predictions</div>
          </div>
          <div className="prediction-table__summary-item">
            <div className="prediction-table__summary-value prediction-table__summary-value--annotated">
              {Object.keys(annotations).filter(id => annotations[id]).length}
            </div>
            <div className="prediction-table__summary-label">Annotated</div>
          </div>
          <div className="prediction-table__summary-item">
            <div className="prediction-table__summary-value prediction-table__summary-value--review">
              {Object.keys(annotations).filter(id => annotations[id]?.annotation === 'review_needed').length}
            </div>
            <div className="prediction-table__summary-label">Review Needed</div>
          </div>
          <div className="prediction-table__summary-item">
            <div className="prediction-table__summary-value prediction-table__summary-value--accurate">
              {Object.keys(annotations).filter(id => annotations[id]?.annotation === 'accurate').length}
            </div>
            <div className="prediction-table__summary-label">Accurate</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PredictionTable;
